import { atom, useRecoilState, useResetRecoilState } from "recoil";
import { useCallback } from "react";
import { randomString } from "../../Functions";
import * as topikAPI from "../../Data/Topik";
import { TopikExamType } from "../../Data/Topik/types";
import { ExamWithStats } from "../../Data/Topik/Exam";
import { localStorageDB } from "@/Data/LocalStorage";

const STORAGE_KEY = 'topik_exams';

type ExamsState = {
    items: Record<string, ExamWithStats>;
    list: string[];
    total: number;
    loading: boolean;
    error: string | null;
};

const validateExamsState = (state: any): state is ExamsState => {
    return state &&
        typeof state.items === 'object' &&
        Array.isArray(state.list) &&
        typeof state.total === 'number' &&
        typeof state.loading === 'boolean';
};

const localStorageEffect = (key: string) => ({ setSelf, onSet }: { setSelf: Function; onSet: Function }) => {
    try {
        const savedValue = localStorageDB.getItem(key);
        if (savedValue) {
            const parsed = JSON.parse(savedValue);
            if (validateExamsState(parsed)) {
                setSelf({ ...parsed, loading: false, error: null });
            }
        }
    } catch (error) {
        console.error('Error loading topik exams from localStorage:', error);
    }

    onSet((newState: ExamsState) => {
        try {
            const { loading, error, ...persistState } = newState;
            localStorageDB.setItem(key, JSON.stringify({ ...persistState, loading: false, error: null }));
        } catch (error) {
            console.error('Error saving topik exams to localStorage:', error);
        }
    });
};

export const examsState = atom<ExamsState>({
    key: "topikExamsState" + randomString(4),
    default: {
        items: {},
        list: [],
        total: 0,
        loading: false,
        error: null
    },
    effects: [localStorageEffect(STORAGE_KEY)]
});

export const useExams = () => {
    const [state, setState] = useRecoilState(examsState);
    const resetExams = useResetRecoilState(examsState);

    const syncExams = useCallback(async (params?: {
        limit?: number;
        offset?: number;
    }): Promise<TopikExamType[]> => {
        setState(prev => ({ ...prev, loading: true, error: null }));

        try {
            const result = await topikAPI.listExams(params);

            if (result) {
                const items = Object.fromEntries(result.data.map(e => [e.examid, e]));
                const newIds = result.data.map(e => e.examid);

                setState(prev => {
                    // If offset > 0, append to existing list; otherwise replace
                    const isAppending = (params?.offset ?? 0) > 0;
                    const existingIds = isAppending ? prev.list : [];
                    const uniqueNewIds = newIds.filter(id => !existingIds.includes(id));

                    return {
                        ...prev,
                        items: { ...prev.items, ...items },
                        list: [...existingIds, ...uniqueNewIds],
                        total: result.total,
                        loading: false
                    };
                });

                return result.data;
            }

            setState(prev => ({ ...prev, loading: false }));
            return [];
        } catch (error) {
            console.error('Failed to sync exams:', error);
            setState(prev => ({ ...prev, loading: false, error: 'Failed to load exams' }));
            return [];
        }
    }, [setState]);

    const syncExam = useCallback(async (params: {
        examid: string;
    }): Promise<ExamWithStats | null> => {
        setState(prev => ({ ...prev, loading: true, error: null }));

        try {
            const exam = await topikAPI.getExam(params);

            if (exam) {
                setState(prev => ({
                    ...prev,
                    items: { ...prev.items, [exam.examid]: exam },
                    loading: false
                }));

                return exam;
            }

            setState(prev => ({ ...prev, loading: false }));
            return null;
        } catch (error) {
            console.error('Failed to sync exam:', error);
            setState(prev => ({ ...prev, loading: false, error: 'Failed to load exam' }));
            return null;
        }
    }, [setState]);

    const allocateExam = useCallback(async (params: {
        examid: string;
        userid?: string;
        addressid?: string;
    }) => {
        setState(prev => ({ ...prev, loading: true, error: null }));

        try {
            const result = await topikAPI.allocateExam(params);

            setState(prev => ({ ...prev, loading: false }));
            return result;
        } catch (error) {
            console.error('Failed to allocate exam:', error);
            setState(prev => ({ ...prev, loading: false, error: 'Failed to allocate exam' }));
            return null;
        }
    }, [setState]);

    const getExam = useCallback((examid: string): ExamWithStats | null => {
        return state.items[examid] ?? null;
    }, [state.items]);

    const reset = useCallback(() => {
        resetExams();
    }, [resetExams]);

    return {
        exams: state.items,
        examList: state.list.map(id => state.items[id]).filter(Boolean),
        total: state.total,
        loading: state.loading,
        error: state.error,
        syncExams,
        syncExam,
        allocateExam,
        getExam,
        reset
    };
};
